import React, { useRef, useState, useEffect, useMemo } from "react";
import { useOutletContext, useLocation, useNavigate } from "react-router-dom";
import {
  UserRound,
  Mail,
  Phone,
  AtSign,
  ShieldCheck,
  CalendarDays,
  Heart,
  Camera,
  CheckCircle2,
  Loader2,
  Eye,
  EyeOff,
  Lock,
  ArrowRight,
  AlertCircle,
  Zap,
} from "lucide-react";
import UserAvatar from "../components/UserAvatar";
import FavoritePlaceCard, { FavoriteEmptyState } from "../components/FavoritePlaceCard";
import { useAppContext } from "../store/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { userService } from "../services/userService";
import { favoriteService } from "../services/favoriteService";
import { authService } from "../services/authService";

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="group relative overflow-hidden rounded-xl border-2 border-amber-100 bg-white px-3 py-1.5 shadow-sm transition duration-300 hover:border-nelson hover:shadow-md sm:px-4 sm:py-2">
    <div className="absolute inset-0 bg-gradient-to-r from-nelson/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
    <div className="relative flex items-start gap-3">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-bg-section transition-colors group-hover:bg-nelson/20">
        <Icon size={15} className="text-nelson transition-colors group-hover:text-nelson" />
      </div>
      <div className="min-w-0">
        <p className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-text-soft">{label}</p>
        <p className="mt-0.5 truncate text-sm font-semibold text-text-main">
          {value || <span className="italic text-gray-400">Chưa cập nhật</span>}
        </p>
      </div>
    </div>
  </div>
);

const PasswordField = ({ label, value, onChange, show, onToggle, placeholder, autoComplete, strength = null }) => (
  <div className="space-y-1">
    <label className="block text-xs font-bold text-nelson sm:text-sm">{label}</label>
    <div className="group relative flex items-center gap-2 rounded-xl border-2 border-amber-100 bg-white px-3 py-2 shadow-sm transition duration-300 hover:border-nelson focus-within:border-nelson focus-within:ring-2 focus-within:ring-nelson/20 sm:gap-3 sm:px-3.5 sm:py-2">
      <Lock size={16} className="shrink-0 text-text-soft transition-colors group-focus-within:text-nelson sm:h-[18px] sm:w-[18px]" />
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="min-w-0 flex-1 bg-transparent text-sm text-text-main outline-none placeholder:text-text-soft"
        required
      />
      <button type="button" onClick={onToggle} className="shrink-0 text-text-soft transition hover:text-nelson">
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
    {strength && (
      <div className="flex items-center gap-2 pt-0.5">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-bg-section">
          <div
            className={`h-full transition-all duration-300 ${
              strength === "weak" ? "w-1/3 bg-red-400" : strength === "medium" ? "w-2/3 bg-amber-400" : "w-full bg-nelson"
            }`}
          />
        </div>
        <span
          className={`text-[0.65rem] font-semibold sm:text-xs ${strength === "weak" ? "text-red-500" : strength === "medium" ? "text-amber-500" : "text-nelson"}`}
        >
          {strength === "weak" ? "Yếu" : strength === "medium" ? "Trung bình" : "Mạnh"}
        </span>
      </div>
    )}
  </div>
);

const PasswordStrengthItem = ({ text, ok }) => (
  <div className={`flex items-center gap-1.5 transition ${ok ? "text-nelson" : "text-text-soft"}`}>
    <div
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[0.6rem] font-bold transition sm:h-5 sm:w-5 sm:text-xs ${ok ? "bg-nelson text-white" : "border border-amber-100 bg-white"}`}
    >
      {ok && "✓"}
    </div>
    <span className="text-[0.7rem] leading-snug sm:text-xs">{text}</span>
  </div>
);

const Account = () => {
  const { profile, refetch } = useOutletContext();
  const { token, updateUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { favorites = [], locations = [], toggleFavorite } = useAppContext();
  const passwordRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [avatarBust, setAvatarBust] = useState(undefined);
  const [uploadError, setUploadError] = useState("");
  const [favoriteCount, setFavoriteCount] = useState(profile?.favorites?.length ?? 0);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const fileRef = useRef(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [show3, setShow3] = useState(false);
  const [submittingPw, setSubmittingPw] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");

  const userId = profile?._id || profile?.id;

  const favoriteItems = useMemo(() => {
    const byId = new Map((Array.isArray(locations) ? locations : []).map((l) => [String(l.id), l]));
    return (Array.isArray(favorites) ? favorites : []).map((id) => byId.get(String(id))).filter(Boolean);
  }, [favorites, locations]);

  useEffect(() => {
    const loadFavoriteCount = async () => {
      if (!userId || !token) return;
      setLoadingFavorites(true);
      try {
        const data = await favoriteService.getFavoriteCount(token);
        setFavoriteCount(data.count);
      } catch (err) {
        console.error("Error loading favorite count:", err);
        setFavoriteCount(profile?.favorites?.length ?? 0);
      } finally {
        setLoadingFavorites(false);
      }
    };

    loadFavoriteCount();
  }, [userId, token, profile?.favorites?.length]);

  useEffect(() => {
    if (!profile) return;
    if (location.hash === "#doi-mat-khau" && passwordRef.current) {
      passwordRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash, profile]);

  if (!profile) return null;

  const joinDate = profile.createdAt
    ? new Date(profile.createdAt).toLocaleDateString("vi-VN", { year: "numeric", month: "long", day: "numeric" })
    : null;

  const passwordStrength = {
    hasMinLength: newPassword.length >= 8,
    hasUpperLower: /[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword),
    hasNumber: /\d/.test(newPassword),
    hasSpecial: /[!@#$%^&*]/.test(newPassword),
  };
  const strengthScore = Object.values(passwordStrength).filter(Boolean).length;
  let strength = null;
  if (newPassword.length > 0) {
    if (strengthScore <= 1) strength = "weak";
    else if (strengthScore <= 2) strength = "medium";
    else strength = "strong";
  }

  const handleAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !token) return;
    setUploadError("");
    setUploading(true);
    try {
      const { user } = await userService.uploadAvatar(token, file);
      updateUser({
        id: user._id || user.id,
        fullName: user.fullName,
        email: user.email,
        username: user.username,
        role: user.role,
        avatar: user.avatar,
        authProvider: user.authProvider,
      });
      setAvatarBust(Date.now());
      await refetch();
    } catch (err) {
      setUploadError(err.message || "Upload ảnh đại diện thất bại");
    } finally {
      setUploading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwError("");
    setPwSuccess("");

    if (newPassword.length < 8) {
      setPwError("Mật khẩu mới tối thiểu 8 ký tự.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError("Mật khẩu xác nhận không khớp.");
      return;
    }

    setSubmittingPw(true);
    try {
      const data = await authService.changePassword(token, { currentPassword, newPassword });
      setPwSuccess(data.message || "Đã cập nhật mật khẩu thành công.");
      updateUser({ mustChangePassword: false });
      refetch?.();
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setPwError(err.message || "Đã có lỗi xảy ra.");
    } finally {
      setSubmittingPw(false);
    }
  };

  const isGoogle = profile?.authProvider === "google";

  return (
    <>
    {profile?.mustChangePassword ? (
      <div role="alert" className="mx-auto mb-3 w-full max-w-6xl rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <p className="font-bold">Bạn đang dùng mật khẩu tạm do quản trị viên cấp.</p>
        <p className="mt-0.5">Vì an toàn, hãy đổi sang mật khẩu của riêng bạn ở mục “Đổi mật khẩu” bên dưới trước khi tiếp tục sử dụng.</p>
      </div>
    ) : null}
    <div className="mx-auto grid w-full max-w-6xl gap-3 lg:grid-cols-12 lg:items-start lg:gap-4">
      {/* Cột trái: hồ sơ + thông tin + yêu thích */}
      <div className="space-y-2 sm:space-y-3 lg:col-span-7">
        <div className="overflow-hidden rounded-2xl border border-amber-100 bg-gradient-to-br from-nelson via-primary-light to-bg-main shadow-lg">
          <div className="relative px-4 py-3 sm:px-5 sm:py-4">
            <div className="absolute top-0 right-0 h-40 w-40 rounded-full bg-white/5 blur-3xl -mr-20 -mt-20" />
            <div className="absolute bottom-0 left-0 h-32 w-32 rounded-full bg-white/5 blur-3xl -mb-16 -ml-16" />

            <div className="relative">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="relative">
                    <UserAvatar
                      url={profile.avatar}
                      name={profile.fullName}
                      cacheBust={avatarBust}
                      sizeClass="h-14 w-14 sm:h-16 sm:w-16"
                      textClass="text-2xl"
                      roundedClass="rounded-2xl"
                      imgClassName="shadow-lg"
                    />
                    <button
                      type="button"
                      disabled={uploading}
                      onClick={() => fileRef.current?.click()}
                      className="absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full bg-white text-nelson shadow-lg transition hover:scale-110 disabled:opacity-60"
                      title="Đổi ảnh đại diện"
                    >
                      <Camera size={16} className="font-semibold" />
                    </button>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={handleAvatarFile}
                    />
                  </div>
                  <div>
                    <p className="text-[0.65rem] font-semibold uppercase tracking-widest text-white/70 sm:text-xs">Hồ sơ cá nhân</p>
                    <h1 className="mt-0.5 text-xl font-bold text-white sm:text-2xl">{profile.fullName}</h1>
                    <p className="mt-1 text-sm text-white/80">{profile.email}</p>
                    {profile.role === "admin" && (
                      <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold text-white">
                        <CheckCircle2 size={12} />
                        Quản trị viên
                      </div>
                    )}
                  </div>
                </div>

                <div className="text-right">
                  {uploadError && (
                    <div className="mb-2 rounded-lg bg-red-500/20 px-3 py-2 text-xs font-semibold text-red-100">{uploadError}</div>
                  )}
                  <p className="text-[0.7rem] text-white/60">JPEG, PNG, WebP, GIF · tối đa 5MB</p>
                  {uploading && <p className="mt-1 text-xs font-semibold text-white/80">Đang tải lên...</p>}
                </div>
              </div>

              <div className="mt-3 grid grid-cols-3 gap-1.5 sm:gap-2">
                {[
                  { label: "Yêu thích", value: loadingFavorites ? "..." : favoriteCount },
                  { label: "Vai trò", value: profile.role === "admin" ? "Admin" : "Thành viên" },
                  { label: "Tham gia", value: joinDate ? new Date(profile.createdAt).getFullYear() : "—" },
                ].map((s) => (
                  <div
                    key={s.label}
                    className="group relative cursor-pointer overflow-hidden rounded-xl border border-white/20 bg-white/10 px-2 py-2 text-center backdrop-blur-sm transition hover:bg-white/20 sm:rounded-2xl sm:px-3 sm:py-2.5"
                  >
                    <div className="absolute inset-0 bg-white/5 opacity-0 transition-opacity group-hover:opacity-100" />
                    <div className="relative">
                      <p className="flex items-center justify-center gap-2 text-lg font-bold text-white sm:text-xl md:text-2xl">
                        {s.value === "..." ? <Loader2 size={18} className="animate-spin" /> : s.value}
                      </p>
                      <p className="mt-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-white/70 sm:text-[0.65rem]">{s.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-amber-100 bg-card shadow-lg">
          <div className="border-b border-amber-100 bg-gradient-to-r from-nelson/5 to-transparent px-4 py-2.5 sm:px-5 sm:py-3">
            <h2 className="text-sm font-bold text-nelson sm:text-base">Thông tin tài khoản</h2>
            <p className="mt-0.5 text-[0.7rem] text-text-soft sm:text-xs">Thông tin hiển thị từ hồ sơ của bạn</p>
          </div>
          <div className="grid gap-1.5 p-2.5 sm:grid-cols-2 sm:gap-2 sm:p-3">
            <InfoRow icon={UserRound} label="Họ và tên" value={profile.fullName} />
            <InfoRow icon={Mail} label="Email" value={profile.email} />
            <InfoRow icon={AtSign} label="Tên đăng nhập" value={profile.username} />
            <InfoRow icon={Phone} label="Số điện thoại" value={profile.phone} />
            <InfoRow icon={ShieldCheck} label="Vai trò" value={profile.role === "admin" ? "Quản trị viên" : "Thành viên"} />
            {joinDate && <InfoRow icon={CalendarDays} label="Ngày tham gia" value={joinDate} />}
          </div>
        </div>

        <section id="yeu-thich" className="overflow-hidden rounded-2xl border border-[#d9ccb4] bg-[#f7f1e6] shadow-md">
          <div className="flex items-center justify-between gap-3 border-b border-[#d9ccb4] bg-[#fffaf0] px-4 py-3 sm:px-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#f6e6e2]">
                <Heart size={18} className="fill-current text-[#a85d5d]" />
              </div>
              <div>
                <h2 className="font-faustina text-lg font-bold text-[#2f352c]">Địa điểm yêu thích</h2>
                <p className="text-xs text-[#7a7467]">
                  {loadingFavorites ? (
                    <span className="inline-flex items-center gap-1">
                      <Loader2 size={12} className="animate-spin" /> Đang tải...
                    </span>
                  ) : (
                    `${favoriteItems.length || favoriteCount} địa điểm đã lưu`
                  )}
                </p>
              </div>
            </div>
            {favoriteItems.length > 0 && (
              <button
                type="button"
                onClick={() => navigate("/map?fav=1")}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#5f745f] px-3.5 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-[#4f6250]"
              >
                Xem trên bản đồ
                <ArrowRight size={14} />
              </button>
            )}
          </div>

          <div className="p-3 sm:p-4">
            {favorites.length === 0 ? (
              <FavoriteEmptyState onExplore={() => navigate("/map")} />
            ) : favoriteItems.length === 0 ? (
              <p className="py-6 text-center text-sm text-[#7a7467]">Đang tải danh sách địa điểm…</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {favoriteItems.map((loc) => (
                  <FavoritePlaceCard
                    key={loc.id}
                    loc={loc}
                    onOpen={() => navigate(`/location/${encodeURIComponent(loc.slug)}`)}
                    onShowOnMap={() => navigate(`/map?locationId=${encodeURIComponent(loc.id)}`)}
                    onRemove={() => toggleFavorite?.(loc.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Cột phải: đổi mật khẩu */}
      <div ref={passwordRef} id="doi-mat-khau" className="min-w-0 scroll-mt-4 lg:col-span-5">
        {isGoogle && (
          <div className="mb-2 overflow-hidden rounded-xl border-2 border-bg-main/30 bg-bg-section/30 px-3 py-2 text-text-main shadow-md sm:px-4">
            <div className="flex items-start gap-2">
              <Zap size={18} className="mt-0.5 shrink-0 text-text-soft" />
              <div>
                <p className="text-sm font-bold">Tài khoản Google</p>
                <p className="mt-0.5 text-xs text-text-soft">Không có mật khẩu để đổi.</p>
              </div>
            </div>
          </div>
        )}

        <div className={`overflow-hidden rounded-2xl border border-amber-100 bg-card shadow-lg ${isGoogle ? "pointer-events-none opacity-50" : ""}`}>
          <div className="relative overflow-hidden border-b border-amber-100/80 bg-gradient-to-br from-nelson via-primary-light to-bg-main px-3 py-2.5 sm:px-4 sm:py-3">
            <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-white/10 blur-2xl" />
            <div className="relative flex items-center gap-2.5 sm:gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/20 backdrop-blur-sm sm:h-10 sm:w-10">
                <Lock size={18} className="text-white sm:h-5 sm:w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-[0.6rem] font-semibold uppercase tracking-widest text-white/70 sm:text-[0.65rem]">Bảo mật</p>
                <h2 className="text-lg font-bold leading-tight text-white sm:text-xl">Đổi mật khẩu</h2>
              </div>
            </div>
          </div>

          <form onSubmit={handlePasswordSubmit} className="space-y-2.5 p-3 sm:space-y-3 sm:p-4">
            <PasswordField
              label="Mật khẩu hiện tại"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              show={show1}
              onToggle={() => setShow1((v) => !v)}
              placeholder="Nhập mật khẩu hiện tại"
              autoComplete="current-password"
            />

            <div className="h-px bg-gradient-to-r from-transparent via-amber-100 to-transparent" />

            <PasswordField
              label="Mật khẩu mới"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              show={show2}
              onToggle={() => setShow2((v) => !v)}
              placeholder="Tối thiểu 8 ký tự"
              autoComplete="new-password"
              strength={strength}
            />

            <PasswordField
              label="Xác nhận mật khẩu mới"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              show={show3}
              onToggle={() => setShow3((v) => !v)}
              placeholder="Nhập lại mật khẩu mới"
              autoComplete="new-password"
            />

            {newPassword.length > 0 && (
              <div className="rounded-xl border border-amber-100 bg-bg-section/40 px-3 py-2 sm:px-3.5">
                <p className="mb-1.5 text-[0.7rem] font-bold text-text-main sm:text-xs">Gợi ý mật khẩu mạnh</p>
                <div className="grid grid-cols-1 gap-x-3 gap-y-1 sm:grid-cols-2">
                  <PasswordStrengthItem text="≥ 8 ký tự" ok={passwordStrength.hasMinLength} />
                  <PasswordStrengthItem text="Chữ hoa & thường" ok={passwordStrength.hasUpperLower} />
                  <PasswordStrengthItem text="Có chữ số" ok={passwordStrength.hasNumber} />
                  <PasswordStrengthItem text="Ký tự đặc biệt" ok={passwordStrength.hasSpecial} />
                </div>
              </div>
            )}

            {pwError && (
              <div className="flex items-start gap-2 rounded-lg border-2 border-accent/30 bg-accent/10 px-3 py-2">
                <AlertCircle size={16} className="mt-0.5 shrink-0 text-accent" />
                <span className="text-xs font-semibold text-accent sm:text-sm">{pwError}</span>
              </div>
            )}

            {pwSuccess && (
              <div className="flex items-start gap-2 rounded-lg border-2 border-nelson bg-nelson/10 px-3 py-2">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-nelson" />
                <span className="text-xs font-semibold text-nelson sm:text-sm">{pwSuccess}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submittingPw}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-nelson px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:bg-primary-hover hover:shadow-lg disabled:opacity-60 sm:w-auto sm:px-6 sm:py-3"
            >
              {submittingPw ? (
                <>
                  <Loader2 size={17} className="animate-spin" />
                  Đang lưu...
                </>
              ) : (
                <>
                  <Zap size={17} />
                  Cập nhật mật khẩu
                  <ArrowRight size={17} />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default Account;
